package hk.com.vr360_demo2.webapp.component.marzipano

import emotion.react.css
import hk.com.vr360_demo2.webapp.component.marzipano.component.EditorHotspotIconWithTip
import js.coroutines.awaitCancellation
import kotlin.js.js
import kotlin.js.unsafeCast
import kotlin.math.PI
import react.CSSProperties
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.useEffect
import react.useRef
import web.cssom.vh
import web.cssom.vw
import web.dom.document
import web.html.HTMLDivElement

data class PanoramaViewParameters(
    val yaw: Double? = null,
    val pitch: Double? = null,
    val fov: Double? = null,
)

data class PanoramaLinkHotspot(
    val hotspotId: String? = null,
    val yaw: Double,
    val pitch: Double,
    val target: String,
    val rotation: Double? = null,
    val imageName: String? = null,
)

// 場景顯示需要的config
data class PanoramaSceneConfig(
    val id: String,
    val url: String,
    val name: String?,
    val cover: String? = null,
    val initialViewParameters: PanoramaViewParameters? = null,
    val linkHotspots: List<PanoramaLinkHotspot>? = null,
    val levels: Array<Level>? = null,
    val faceSize: Int = 2048,
)

/**
 * Result of capturing the current panorama view.
 */
data class ViewCaptureResult(
    val viewParameters: PanoramaViewParameters,
    val snapshot: String?,
)

external interface PanoramaViewerProps : Props {
    var scenes: List<PanoramaSceneConfig>
    var activeSceneIndex:  Int
    var autorotateEnabled: Boolean?
    var onSceneChange: ((Int) -> Unit)?
    /**
     * Called once when the viewer is ready, with a synchronous function to
     * capture the current view (parameters + snapshot). The parent stores
     * this function in a ref and calls it directly — no state round-trip,
     * no re-render, no flicker.
     */
    var onCaptureReady: ((() -> ViewCaptureResult?) -> Unit)?
}

private data class AutoRotateControl(
    val start: () -> Unit,
    val stop: () -> Unit,
)

private val EmptyAutoRotateControl = AutoRotateControl(start = {}, stop = {})
private const val HOTSPOT_ICON_URL = "/assets/images/hotspot/icon-1.png"

private fun clampIndex(index: Int, size: Int): Int {
    if (size <= 0) return 0
    return index.coerceIn(0, size - 1)
}

val PanoramaViewer = FC<PanoramaViewerProps> { props ->
    val viewerRef = useRef<HTMLDivElement>()
    val viewerInstanceRef = useRef<Marzipano.Viewer>()
    val scenesRef = useRef<Array<Scene>>()
    val currentSceneIndexRef = useRef<Int>()
    val canAutoRotateRef = useRef<Boolean>()
    val autoRotateControlRef = useRef<AutoRotateControl>()
    val switchSceneRef = useRef<(Int, Boolean) -> Unit>()
    val resumeTimeoutRef = useRef<dynamic>()

    useEffect(dependencies = arrayOf(props.scenes, props.autorotateEnabled)) {
        val container = viewerRef.current ?: return@useEffect
        if (props.scenes.isEmpty()) {
            container.asDynamic().innerHTML = ""
            return@useEffect
        }

        // Prevent scrollbars at root level
        document.documentElement.asDynamic().style.overflow = "hidden"
        document.documentElement.asDynamic().style.height = "100%"
        document.body.asDynamic().style.overflow = "hidden"
        document.body.asDynamic().style.height = "100%"
        document.body.asDynamic().style.margin = "0"
        document.body.asDynamic().style.padding = "0"

        val clearResumeTimeout: () -> Unit = {
            val timeout = resumeTimeoutRef.current
            if (timeout != null) {
                js("clearTimeout")(timeout)
                resumeTimeoutRef.current = null
            }
        }

        val scheduleAutoRotateResume: () -> Unit = {
            clearResumeTimeout()
            resumeTimeoutRef.current = js("setTimeout")({
                autoRotateControlRef.current?.start?.invoke()
                resumeTimeoutRef.current = null
            }, 3000)
        }

        container.asDynamic().innerHTML = ""
        // preserveDrawingBuffer is REQUIRED — without it WebGL clears the drawing buffer
        // after every frame and toDataURL() / takeSnapshot() returns a black image.
        val viewerOpts = js("{}")
        viewerOpts.stage = js("{}")
        viewerOpts.stage.preserveDrawingBuffer = true
        val viewer = Marzipano.Viewer(container, viewerOpts)
        viewerInstanceRef.current = viewer

        val autorotate = Marzipano.autorotate(autoRotateOptions())
        canAutoRotateRef.current = props.autorotateEnabled != false

        fun stopAutoRotate() {
            viewer.stopMovement()
        }

        fun startAutoRotate() {
            if (canAutoRotateRef.current == true) {
                viewer.startMovement(autorotate)
            }
        }

        autoRotateControlRef.current = AutoRotateControl(start = ::startAutoRotate, stop = ::stopAutoRotate)

        val sceneIndexesById = mutableMapOf<String, Int>()
        props.scenes.forEachIndexed { index, sceneConfig ->
            sceneIndexesById[sceneConfig.id] = index
        }

        switchSceneRef.current = switchScene@{ index: Int, notify: Boolean ->
            val sceneList = scenesRef.current ?: return@switchScene
            if (sceneList.isEmpty()) return@switchScene
            val safeIndex = clampIndex(index, sceneList.size)
            val initialView = props.scenes.getOrNull(safeIndex)?.initialViewParameters
            applyInitialViewToScene(sceneList[safeIndex], initialView)

            sceneList[safeIndex].switchTo(switchOptions(300)) // 動畫時長 300ms
            currentSceneIndexRef.current = safeIndex

            if (notify) {
                props.onSceneChange?.invoke(safeIndex)
            }

            autoRotateControlRef.current?.stop?.invoke()
            if (canAutoRotateRef.current == true) {
                autoRotateControlRef.current?.start?.invoke()
            }
        }

        val React = js("require('react')")
        val reactDomClient = js("require('react-dom/client')")

        val createdScenes = mutableListOf<Scene>()
        props.scenes.forEach { sceneConfig ->

//            val source = Marzipano.ImageUrlSource.fromString(
//                "/test/titles/{z}/{f}/{y}/{x}.jpg",
//                imageSourceOptions("/test/titles")
//            )
            val source = Marzipano.ImageUrlSource.fromString(
                "${sceneConfig.url}/{z}/{f}/{y}/{x}.jpg",
                imageSourceOptions(sceneConfig.url)
            )

            val geometry = Marzipano.CubeGeometry(sceneConfig.levels ?: defaultLevels())
            val limiter = Marzipano.RectilinearView.limit.traditional(sceneConfig.faceSize, (140 * PI) / 180)
            val view = Marzipano.RectilinearView(initialViewParams(sceneConfig.initialViewParameters), limiter)

            val scene = viewer.createScene(
                sceneOptions(
                    source = source,
                    geometry = geometry,
                    view = view
                )
            )

            sceneConfig.linkHotspots.orEmpty().forEach { hotspotConfig ->
                val targetIndex = sceneIndexesById[hotspotConfig.target] ?: return@forEach
                val hotspotDiv = document.createElement("div")
                hotspotDiv.asDynamic().className = "marzipano-tool__linkHotspot"
                hotspotDiv.asDynamic().style.display = "block"
                hotspotDiv.asDynamic().style.pointerEvents = "auto"
                hotspotDiv.asDynamic().style.cursor = "pointer"
                val hotspotProps = js("{}")
                hotspotProps.iconUrl = hotspotConfig.imageName ?: HOTSPOT_ICON_URL
                hotspotProps.tipText = props.scenes[targetIndex].name ?: "link"
                val hotspotElement = React.createElement(EditorHotspotIconWithTip, hotspotProps)
                reactDomClient.createRoot(hotspotDiv).render(hotspotElement)
                hotspotDiv.asDynamic().onclick = {
                    switchSceneRef.current?.invoke(targetIndex, true)
                }
                scene.hotspotContainer().createHotspot(
                    hotspotDiv.unsafeCast<web.dom.Element>(),
                    hotspotPosition(hotspotConfig.yaw, hotspotConfig.pitch)
                )
            }

            createdScenes += scene
        }

        scenesRef.current = createdScenes.toTypedArray()

        if (createdScenes.isNotEmpty()) {
            val initialIndex = clampIndex(props.activeSceneIndex  ?: 0, createdScenes.size)
            switchSceneRef.current?.invoke(initialIndex, false)
            startAutoRotate()
        }

        val handleActivity: () -> Unit = {
            autoRotateControlRef.current?.stop?.invoke()
            scheduleAutoRotateResume()
        }

//        container.asDynamic().addEventListener("mousemove", handleActivity)
        container.asDynamic().addEventListener("mousedown", handleActivity)
        container.asDynamic().addEventListener("mouseup", handleActivity)
        container.asDynamic().addEventListener("touchstart", handleActivity)
        container.asDynamic().addEventListener("touchmove", handleActivity)
        container.asDynamic().addEventListener("touchend", handleActivity)

        // Register synchronous capture function — reads from refs so always up-to-date
        val captureCurrentView: () -> ViewCaptureResult? = captureCurrentView@{
            val v = viewerInstanceRef.current ?: return@captureCurrentView null
            val sceneList = scenesRef.current ?: return@captureCurrentView null
            if (sceneList.isEmpty()) return@captureCurrentView null

            val safeIndex = clampIndex(currentSceneIndexRef.current ?: 0, sceneList.size)
            val scene = sceneList[safeIndex]

            val viewParams = try {
                val p = scene.asDynamic().view().parameters()
                PanoramaViewParameters(
                    yaw = p.yaw as? Double,
                    pitch = p.pitch as? Double,
                    fov = p.fov as? Double,
                )
            } catch (_: dynamic) {
                PanoramaViewParameters()
            }

            val snapshot: String? = try {
                val stage = v.asDynamic().stage()
                val snap = stage?.takeSnapshot(js("({quality:80})"))?.unsafeCast<String>()
                if (snap != null && snap.length > 128) snap else null
            } catch (_: dynamic) {
                null
            }

            ViewCaptureResult(viewParams, snapshot)
        }
        props.onCaptureReady?.invoke(captureCurrentView)

        awaitCancellation {
            autoRotateControlRef.current?.stop?.invoke()
            clearResumeTimeout()
//            container.asDynamic().removeEventListener("mousemove", handleActivity)
            container.asDynamic().removeEventListener("mousedown", handleActivity)
            container.asDynamic().removeEventListener("mouseup", handleActivity)
            container.asDynamic().removeEventListener("touchstart", handleActivity)
            container.asDynamic().removeEventListener("touchmove", handleActivity)
            container.asDynamic().removeEventListener("touchend", handleActivity)

            viewer.destroy()
            container.asDynamic().innerHTML = ""
            scenesRef.current = null
            switchSceneRef.current = null
            autoRotateControlRef.current = EmptyAutoRotateControl
            resumeTimeoutRef.current = null
            viewerInstanceRef.current = null
        }
    }


    useEffect(dependencies = arrayOf(props.activeSceneIndex)) {
        val requestedIndex = props.activeSceneIndex ?: return@useEffect
        if (requestedIndex == currentSceneIndexRef.current) return@useEffect
        switchSceneRef.current?.invoke(requestedIndex, false)
    }
 //   val (activeSceneIndex, setActiveSceneIndex) = props.activeSceneIndex

    div {
        css {
            width = 100.vw
            height = 100.vh
//            background = Colors.Background.Semitransparent
            overflow = web.cssom.Overflow.hidden
            asDynamic()["@supports (height: 100dvh)"] =
                js("{}").unsafeCast<CSSProperties>().apply {
                    asDynamic().height = "100dvh"
                }
        }
        onClick = {
//            val nextIndex = if (props.scenes.isNotEmpty()) (activeSceneIndex + 1) % props.scenes.size else 0
//            setActiveSceneIndex(nextIndex)
        }
        ref = viewerRef
    }
}

private fun autoRotateOptions(): AutoRotateOptions {
    val options = js("{}")
    options.yawSpeed = 0.05
    options.targetPitch = 0.0
    options.targetFov = PI / 2
    return options.unsafeCast<AutoRotateOptions>()
}

private fun imageSourceOptions(baseUrl: String): ImageUrlSourceOptions {
    val options = js("{}")
    options.cubeMapPreviewUrl = "$baseUrl/preview.jpg"
    return options.unsafeCast<ImageUrlSourceOptions>()
}

private fun initialViewParams(viewParameters: PanoramaViewParameters?): dynamic {
    val params = js("{}")
    params.yaw = viewParameters?.yaw ?: 0.0
    params.pitch = viewParameters?.pitch ?: 0.0
    params.fov = viewParameters?.fov ?: 3.14159 // 默認 1.2，視野更廣
    return params
}

/** Reset a scene's view to [viewParameters] before [Scene.switchTo] so revisits use the configured initial view. */
private fun applyInitialViewToScene(scene: Scene, viewParameters: PanoramaViewParameters?) {
    try {
        val view = scene.asDynamic().view()
        val params = view.parameters()
        val initial = initialViewParams(viewParameters)
        params.yaw = initial.yaw
        params.pitch = initial.pitch
        params.fov = initial.fov
        view.setParameters(params)
    } catch (_: dynamic) {
        // Marzipano view API unavailable — keep whatever view state the scene already has
    }
}

private fun sceneOptions(
    source: Source,
    geometry: Marzipano.CubeGeometry,
    view: Marzipano.RectilinearView,
): SceneOptions {
    val options = js("{}")
    options.source = source
    options.geometry = geometry
    options.view = view
    options.pinFirstLevel = true
    return options.unsafeCast<SceneOptions>()
}

private fun switchOptions(transitionDuration: Int): SwitchOptions {
    val options = js("{}")
    options.transitionDuration = transitionDuration
    return options.unsafeCast<SwitchOptions>()
}

private fun hotspotPosition(yaw: Double, pitch: Double): HotspotPosition {
    val position = js("{}")
    position.yaw = yaw
    position.pitch = pitch
    return position.unsafeCast<HotspotPosition>()
}

private fun defaultLevels(): Array<Level> = arrayOf(
    level(tileSize = 256, size = 256, fallbackOnly = true),
    level(tileSize = 512, size = 512 ),
    level(tileSize = 512, size = 1024),
    level(tileSize = 512, size = 2048),
//    level(tileSize = 512, size = 4096)
)

private fun level(tileSize: Int, size: Int, fallbackOnly: Boolean? = null): Level {
    val level = js("{}")
    level.tileSize = tileSize
    level.size = size
    if (fallbackOnly != null) {
        level.fallbackOnly = fallbackOnly
    }
    return level.unsafeCast<Level>()
}
